import { CheckCircle2, Circle, Loader2, XCircle, AlertTriangle } from 'lucide-react';
import type { BootState, BootCheckResult } from '@/lib/api';

/**
 * Human-readable labels for each boot check. Keys match the `name` field
 * set in `packages/server/src/boot/checks/*.ts`. Unknown keys fall back
 * to the raw key so a new check added server-side surfaces with a
 * best-effort label rather than disappearing.
 */
const CHECK_LABELS: Record<string, string> = {
  'db-schema': 'Database schema',
  vault: 'Encrypted vault',
  'bundled-workflows': 'Bundled workflow library',
  'provider-auth': 'AI provider credentials',
  'auth-bootstrap': 'Operator authentication',
  'adapter-health': 'Platform adapters',
  'isolation-home': 'Workspace storage',
};

function labelFor(key: string): string {
  return CHECK_LABELS[key] ?? key;
}

function CheckRow({ name, result }: { name: string; result: BootCheckResult }): React.ReactElement {
  const { status, detail, durationMs } = result;
  // PascalCase identifier is required so React can tell this is a
  // component and not an HTML element at the JSX call site below.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const Icon =
    status === 'ok'
      ? CheckCircle2
      : status === 'degraded'
        ? AlertTriangle
        : status === 'failed'
          ? XCircle
          : status === 'running'
            ? Loader2
            : Circle;

  const tone =
    status === 'ok'
      ? 'text-success'
      : status === 'degraded'
        ? 'text-warning'
        : status === 'failed'
          ? 'text-error'
          : status === 'running'
            ? 'text-primary'
            : 'text-text-tertiary';

  const animate = status === 'running' ? 'animate-spin' : '';

  return (
    <li className="flex items-start gap-3 py-2">
      <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${tone} ${animate}`} />
      <span className="sr-only">{status}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-text-primary">{labelFor(name)}</span>
          {typeof durationMs === 'number' && status !== 'pending' && status !== 'running' && (
            <span className="text-[11px] text-text-tertiary font-mono">{durationMs}ms</span>
          )}
        </div>
        {detail && <div className="text-xs text-text-secondary mt-0.5 break-words">{detail}</div>}
      </div>
    </li>
  );
}

export interface BootSplashProps {
  state: BootState | undefined;
  isError: boolean;
  error: Error | null;
}

/**
 * Full-viewport splash rendered while the server-side `/api/ready`
 * endpoint reports anything other than `ready` or `degraded`.
 *
 * Surfaces per-check progress so operators can see which subsystem is
 * slow or broken. If a critical check fails, the `detail` field on the
 * failed check is shown as the actionable hint (populated server-side
 * in `packages/server/src/boot/checks/*.ts`).
 */
export function BootSplash({ state, isError, error }: BootSplashProps): React.ReactElement {
  const phase = state?.phase ?? 'starting';
  const checks = state?.checks ?? {};
  const orderedKeys = Object.keys(checks).sort();

  let headline: string;
  let headlineTone: string;
  if (isError) {
    headline = 'Cannot reach platform server';
    headlineTone = 'text-error';
  } else if (phase === 'failed') {
    headline = 'Platform initialization failed';
    headlineTone = 'text-error';
  } else if (phase === 'starting') {
    headline = 'Maestro-Prime is starting…';
    headlineTone = 'text-text-primary';
  } else if (phase === 'verifying') {
    headline = 'Verifying platform subsystems…';
    headlineTone = 'text-text-primary';
  } else {
    // ready or degraded — splash shouldn't be visible in those phases, but
    // render something defensible if we get here via a transient race.
    headline = 'Maestro-Prime is ready.';
    headlineTone = 'text-success';
  }

  const elapsedMs =
    state?.completedAt && state.startedAt
      ? new Date(state.completedAt).getTime() - new Date(state.startedAt).getTime()
      : state?.startedAt
        ? Date.now() - new Date(state.startedAt).getTime()
        : 0;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8">
      <div className="w-full max-w-lg">
        <div
          className="sr-only"
          role={isError || phase === 'failed' ? 'alert' : 'status'}
          aria-live="polite"
          aria-atomic="true"
        >
          {headline}.{' '}
          {orderedKeys
            .map(key => {
              const result = checks[key];
              return `${labelFor(key)}: ${result.status}${result.detail ? `, ${result.detail}` : ''}`;
            })
            .join('. ')}
        </div>
        {/* Brand */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
            <span className="text-lg font-semibold text-primary-foreground">M</span>
          </div>
          <span className="text-lg font-semibold text-text-primary">Maestro-Prime</span>
        </div>

        {/* Headline */}
        <h1 className={`text-lg font-semibold ${headlineTone}`}>{headline}</h1>
        <p className="text-sm text-text-secondary mt-1">
          {isError
            ? 'The platform server is unreachable. Check that the Docker container is running and healthy.'
            : phase === 'failed'
              ? 'One or more critical subsystems could not be initialized. See per-check detail below.'
              : 'Checking database, vault, workflow library, AI providers, and adapter health before the UI loads.'}
        </p>

        {/* Check list */}
        {orderedKeys.length > 0 && (
          <ul className="mt-6 divide-y divide-border rounded-lg border border-border bg-surface">
            {orderedKeys.map(k => (
              <CheckRow key={k} name={k} result={checks[k]} />
            ))}
          </ul>
        )}

        {/* Error detail (for fetch failures, not check failures) */}
        {isError && error && (
          <div className="mt-4 rounded-lg border border-error/30 bg-error/10 p-3 text-xs text-error font-mono break-words">
            {error.message}
          </div>
        )}

        {/* Elapsed */}
        {elapsedMs > 0 && (
          <div className="mt-4 text-[11px] text-text-tertiary font-mono">
            Elapsed: {Math.round(elapsedMs)}ms
          </div>
        )}
      </div>
    </div>
  );
}
